import React from 'react'
import { Column } from 'react-table'
import { Element, Button, Icon } from 'react-bulma-components'
import { WeekRowData } from '../types'
import { UserSummaries } from '../../../../api/missions'
import { WorkPeriodEvent } from '../../../../api/work-period-events'
import { buildWorkPeriodEventLabel } from '../../../../utils/work-period-events'
import { MissionWeekIsValidatedMessage } from '../../../missions/mission-messages'
import Message from '../../../message/message'
import { CheckIcon, WarningIcon } from '../../../icons'
import useFilters from '../../../../hooks/filters'
import { FiltersResourcesEnum, initialFilters } from '../../../../store/filters'
import { useNavigate } from 'react-router-dom'
import { getDocIdAsString } from '../../../../utils/documents'
import GenericCell from '../generic-cell'

/**
 * Hook pour définir les colonnes du tableau des semaines
 */
export const useWeeksTableColumns = (
  weeklyHoursWorked?: number,
  employerRules: UserSummaries['weeks'][0]['employerRules'] = [],
  events: WorkPeriodEvent[] = [],
  hasInformativeEvents: boolean = false,
  onCloseWeek?: (weekNumber: number, year: number) => void,
): Column<WeekRowData>[] => {
  const benefitEvents = events.filter(e => e.type === 'benefit')
  const { setFilters } = useFilters(FiltersResourcesEnum.missions)
  const navigate = useNavigate()

  const naviguateToSummary = (data: WeekRowData) => {
    const { weekStartDate, weekEndDate, workerId } = data

    setFilters(FiltersResourcesEnum.missions, {
      ...initialFilters[FiltersResourcesEnum.missions],
      start: new Date(weekStartDate || new Date()),
      end: new Date(weekEndDate || new Date()),
      user: [getDocIdAsString(workerId)],
      summaryMinimalMode: false,
    })
    navigate('/summary')
  }

  return React.useMemo(
    () => [
      {
        Header: 'Sem.',
        accessor: 'weekNumber',
        Cell: ({ row }: { row: { original: WeekRowData } }) => (
          <Element>
            {row.original.isTotalRow ? (
              <Element renderAs="strong">{row.original.weekNumber}</Element>
            ) : (
              <Element renderAs="a" onClick={() => naviguateToSummary(row.original)}>
                <Element renderAs="strong">{row.original.weekNumber}</Element>
              </Element>
            )}
            <br />
            <Element renderAs="small" textColor="grey">
              {row.original.year}
            </Element>
          </Element>
        ),
      },
      {
        Header: 'Période',
        accessor: 'weekStart',
        Cell: ({ row }: { row: { original: WeekRowData } }) => (
          <Element>
            {row.original.weekStart}
            <br />
            {row.original.weekEnd}
          </Element>
        ),
      },
      {
        Header: 'Contrat',
        accessor: 'contractHours',
        Cell: ({ row }: { row: { original: WeekRowData } }) => (
          <Element>
            {row.original.contractHours.value}
            <br />
            <Element renderAs="small" textColor="grey">
              {row.original.contractHours.value ? 'h/sem' : ''}
            </Element>
          </Element>
        ),
      },
      {
        Header: (
          <GenericCell
            title="Travaillé"
            tooltipContent="Heures réellement travaillées pendant la semaine."
          />
        ),
        accessor: 'worked',
        Cell: ({ row }: { row: { original: WeekRowData } }) => (
          <GenericCell cellValue={row.original.worked} />
        ),
      },
      {
        Header: (
          <GenericCell
            title="A déduire"
            tooltipContent={
              <>
                A déduire entre les heures du contrat et la somme des heures travaillées
                <br /> et des heures non effectives.
                <br />{' '}
                <Element renderAs="small">
                  (heures travaillées + heures non effectives) - heures contractuelles.
                </Element>
              </>
            }
          />
        ),
        accessor: 'contractDiff',
        Cell: ({ row }: { row: { original: WeekRowData } }) => (
          <GenericCell cellValue={row.original.contractDiff} />
        ),
      },
      // Colonnes dynamiques pour les employer rules
      ...employerRules.map(rule => ({
        Header: <Element>{rule.shortName}</Element>,
        accessor: `rule_${rule.ruleId}`,
        Cell: ({ row }: { row: { original: WeekRowData } }) => {
          const formattedValue = row.original[`rule_${rule.ruleId}`] as string
          const decimalValue = row.original[`rule_${rule.ruleId}_decimal`] as string

          // Créer une CellValue pour cette règle
          const cellValue = {
            value: formattedValue,
            decimal: rule.kind !== 'goal' ? decimalValue : undefined,
          }

          return <GenericCell cellValue={cellValue} />
        },
      })),
      // Colonnes pour les benefit events
      ...benefitEvents.map(event => ({
        Header: <Element title={buildWorkPeriodEventLabel(event)}>{event.code}</Element>,
        accessor: `event_${event.code}`,
      })),
      // Colonne pour les évènements informatifs (seulement si il y en a)
      ...(hasInformativeEvents
        ? [
            {
              Header: 'Evèn. Info',
              accessor: 'informativeEvents',
            },
          ]
        : []),

      {
        Header: (
          <GenericCell
            title="Compteurs"
            tooltipContent="Impact total sur les compteurs suite à la cloture de la semaine."
          />
        ),
        accessor: 'sentToCounters',
        Cell: ({ row }: { row: { original: WeekRowData } }) => {
          const isTotalRow = row.original.isTotalRow
          const isRecorded = row.original.isRecorded
          if (!isTotalRow && !isRecorded) {
            return <Element textColor="grey">N/A</Element>
          }
          return (
            <GenericCell
              cellValue={row.original.sentToCounters}
              tooltipContent={row.original.accountedSentToCountersTooltip}
            />
          )
        },
      },
      {
        Header: (
          <GenericCell
            title="Compta."
            tooltipContent={
              <>
                Heures comptabilisées suite à la cloture de la semaine. <br />
                Heures travaillées + heures prises ou envoyées aux compteurs.
              </>
            }
          />
        ),
        accessor: 'accounted',
        Cell: ({ row }: { row: { original: WeekRowData } }) => (
          <GenericCell cellValue={row.original.accounted} />
        ),
      },
      {
        Header: 'Statut',
        accessor: 'isValidated',
        Cell: ({ row }: { row: { original: WeekRowData } }) => {
          const { isValidated, isRecorded, weekNumberValue, yearValue, contractHours } =
            row.original
          const canOpen = Boolean(onCloseWeek && weekNumberValue && yearValue)

          // Si pas de données (contractHours === '---'), afficher "Pas de mission"
          if (contractHours.value === '---' || row.original.isTotalRow) {
            return null
          }

          if (!isValidated) {
            return (
              <Message
                text={`Journée(s) non validée(s)`}
                icon={<WarningIcon />}
                iconColor="danger"
                data-test="week-not-validated"
                small
              />
            )
          }

          if (isValidated && !isRecorded) {
            return (
              <GenericCell tooltipContent="Clôturer la semaine">
                <Button
                  color="primary"
                  disabled={!canOpen}
                  data-test={`close-week-button-${weekNumberValue}-${yearValue}`}
                  onClick={() =>
                    canOpen &&
                    onCloseWeek &&
                    onCloseWeek(weekNumberValue as number, yearValue as number)
                  }
                >
                  <Icon>
                    <CheckIcon />
                  </Icon>
                </Button>
              </GenericCell>
            )
          }

          return (
            <Element textColor="success">
              <MissionWeekIsValidatedMessage
                mission={{ weekIsValidated: true }}
                text="Semaine Clôturée"
                small
              />
            </Element>
          )
        },
      },
    ],
    [weeklyHoursWorked, employerRules, events, hasInformativeEvents, onCloseWeek],
  )
}
